import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  Grid,
  MenuItem,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import { ConfirmDialog, TableSkeleton, ActionButton, DataTable, FilterBar } from '../components';

const MotionBox = motion(Box);

function Projects() {
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, projectId: null });
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [formData, setFormData] = useState({
    project_name: '',
    project_code: '',
    company_id: '',
    project_type: '',
    description: '',
    location: '',
    start_date: '',
    end_date: '',
    status: 1,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, companiesRes] = await Promise.all([
        API.projects.list(),
        API.companies.list(),
      ]);
      setProjects(projectsRes?.data || []);
      setCompanies(companiesRes?.data || []);
    } catch (err) {
      showError('Failed to load projects');
      console.error('Projects fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        project_name: project.project_name || '',
        project_code: project.project_code || '',
        company_id: project.company_id || '',
        project_type: project.project_type || '',
        description: project.description || '',
        location: project.location || '',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        status: project.status !== undefined ? project.status : 1,
      });
    } else {
      setEditingProject(null);
      setFormData({
        project_name: '',
        project_code: '',
        company_id: '',
        project_type: '',
        description: '',
        location: '',
        start_date: '',
        end_date: '',
        status: 1,
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingProject(null);
    setFormData({
      project_name: '',
      project_code: '',
      company_id: '',
      project_type: '',
      description: '',
      location: '',
      start_date: '',
      end_date: '',
      status: 1,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingProject) {
        await API.projects.update(editingProject.project_id, formData);
        showSuccess('Project updated successfully!');
      } else {
        // Remove status field when creating a new project
        const { status, ...createData } = formData;
        await API.projects.create(createData);
        showSuccess('Project created successfully!');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (editingProject ? 'Failed to update project' : 'Failed to create project');
      showError(errorMessage);
      console.error('Project submit error:', err);
    }
  };

  const handleDeleteClick = (row) => {
    setConfirmDialog({ open: true, projectId: row.project_id });
  };

  const handleDelete = async () => {
    try {
      await API.projects.delete(confirmDialog.projectId);
      showSuccess('Project deleted successfully!');
      fetchData();
    } catch (err) {
      showError('Failed to delete project');
      console.error('Project delete error:', err);
    }
  };

  const handleStatusToggle = async (projectId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await API.projects.update(projectId, { status: newStatus });
      showSuccess(`Project ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
      fetchData();
    } catch (err) {
      showError('Failed to update project status');
      console.error('Project status update error:', err);
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    return company ? company.company_name : 'Unknown';
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.project_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getCompanyName(project.company_id)?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || project.status === parseInt(statusFilter);
    const matchesType = typeFilter === 'all' || project.project_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const uniqueTypes = [...new Set(projects.map(p => p.project_type).filter(Boolean))];

  const columns = [
    {
      field: 'project_name',
      headerName: 'Project Name',
      width: '15%',
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.project_name}
        </Typography>
      ),
    },
    {
      field: 'project_code',
      headerName: 'Code',
      width: '10%',
      render: (row) => (
        <Chip 
          label={row.project_code} 
          size="small" 
          sx={{ 
            fontWeight: 600,
            fontSize: '0.7rem',
            backgroundColor: '#EEF2FF',
            color: '#4F46E5',
            border: '1px solid #C7D2FE',
          }}
        />
      ),
    },
    {
      field: 'company_id',
      headerName: 'Company',
      width: '13%',
      render: (row) => getCompanyName(row.company_id),
    },
    {
      field: 'project_type',
      headerName: 'Type',
      width: '10%',
      render: (row) => row.project_type || '-',
    },
    {
      field: 'location',
      headerName: 'Location',
      width: '12%',
      render: (row) => row.location || '-',
    },
    {
      field: 'start_date',
      headerName: 'Start Date',
      width: '10%',
      render: (row) => row.start_date ? new Date(row.start_date).toLocaleDateString() : '-',
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      width: '10%',
      render: (row) => row.end_date ? new Date(row.end_date).toLocaleDateString() : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: '10%',
      sortable: false,
      render: (row) => (
        <Switch
          checked={row.status === 1}
          onChange={() => handleStatusToggle(row.project_id, row.status)}
          size="small"
          color="primary"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: '10%',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-',
    },
  ];

  const filterFields = [
    {
      label: 'Status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: '1', label: 'Active' },
        { value: '2', label: 'Completed' },
        { value: '3', label: 'On Hold' },
        { value: '0', label: 'Cancelled' },
      ],
    },
    {
      label: 'Type',
      value: typeFilter,
      onChange: setTypeFilter,
      options: [
        { value: 'all', label: 'All Types' },
        ...uniqueTypes.map(type => ({ value: type, label: type })),
      ],
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'success';
      case 2: return 'primary';
      case 3: return 'warning';
      case 0: return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1: return 'Active';
      case 2: return 'Completed';
      case 3: return 'On Hold';
      case 0: return 'Cancelled';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            Projects
          </Typography>
        </Box>
        <TableSkeleton columns={7} rows={6} />
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Projects
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all your projects and their details
          </Typography>
        </Box>
        <ActionButton
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Project
        </ActionButton>
      </Box>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search projects..."
        filterFields={filterFields}
      />

      <DataTable
        columns={columns}
        data={filteredProjects}
        onEdit={handleOpenDialog}
        onDelete={handleDeleteClick}
        getRowId={(row) => row.project_id}
        emptyMessage="No projects found"
      />

      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
        }}>
          <Typography variant="h6" fontWeight={700}>
            {editingProject ? 'Edit Project' : 'Add New Project'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Name"
                value={formData.project_name}
                onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Code"
                value={formData.project_code}
                onChange={(e) => setFormData({ ...formData, project_code: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Company"
                value={formData.company_id}
                onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="">Select Company</MenuItem>
                {companies.map(company => (
                  <MenuItem key={company.company_id} value={company.company_id}>
                    {company.company_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Type"
                value={formData.project_type}
                onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={2}>Completed</MenuItem>
                <MenuItem value={3}>On Hold</MenuItem>
                <MenuItem value={0}>Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={4}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="outlined" onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" onClick={handleSubmit}>
            {editingProject ? 'Update Project' : 'Create Project'}
          </ActionButton>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, projectId: null })}
        onConfirm={handleDelete}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmText="Delete"
        type="danger"
      />
    </MotionBox>
  );
}

export default Projects;